/**
 * AccountInfoCard Component
 *
 * Account information cards: Password and Account Info.
 * Part of modularized UserAccountManagement component.
 *
 * Note: Subscription info is handled by SubscriptionInfoCard component.
 */

// 1. External dependencies
import React from 'react';
import { Lock, Calendar, Settings } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  Button,
  useDisclosure
} from '@enfomentis/ui';
import { PasswordChangeModal } from '@/components/shared/auth';
import type { User } from '@/types/auth';

/**
 * Props for AccountInfoCard
 */
export interface AccountInfoCardProps {
  user: User;
}

/**
 * Account Info Card Component
 *
 * Displays:
 * - Password (with change modal)
 * - Account Information (created date, last login)
 *
 * Note: Subscription info is handled by SubscriptionInfoCard component.
 */
export const AccountInfoCard: React.FC<AccountInfoCardProps> = ({ user }) => {
  // Password modal state
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  // Format account creation date
  const createdDate = new Date(user.createdAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Format last login date
  const lastLoginDate = new Date(user.lastLoginAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      {/* Password Card */}
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="text-primary flex-shrink-0">
                <Lock size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-text-primary">
                  Password
                </h3>
                <p className="text-text-secondary">
                  ••••••••••••
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="bordered"
                size="lg"
                startContent={<Settings size={16} />}
                onPress={onOpen}
                className="w-full sm:w-auto"
              >
                Change Password
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Account Information Card */}
      <Card shadow="sm">
        <CardBody className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="text-primary flex-shrink-0">
              <Calendar size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Account Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-text-primary">Account Created</p>
                  <p className="text-text-secondary break-words">{createdDate}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-text-primary">Last Login</p>
                  <p className="text-text-secondary break-words">{lastLoginDate}</p>
                </div>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
      />
    </>
  );
};
